import { FloatingPosition, Button as MantineButton } from '@mantine/core'
import React, { ComponentProps, useContext } from 'react'
import { ClassNameValue } from 'tailwind-merge'
import cn from '../../lib/utils/cn'
import Loader from '../common/Loader'
import Tooltip from './Tooltip'
import AppContext from '../../context/appContext'

type VariantType = 'primary' | 'secondary' | 'tertiary' | 'transparent' | 'link' | 'custom'

interface ButtonProps
	extends Omit<ComponentProps<typeof MantineButton>, 'variant'> {
	variant?: VariantType
	loading?: boolean
	disabled?: boolean
	label?: string | null
	loadingLabel?: string | null
	onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
	leftIcon?: React.ReactNode
	rightIcon?: React.ReactNode
	tooltip?: string | null
	tooltipPosition?: FloatingPosition
	tooltipOnDisabled?: boolean
	className?: string
	classNameWrapper?: ClassNameValue
	classNameLabel?: ClassNameValue
	classNameContent?: ClassNameValue
}
// disabled:bg-accent-disabled
export const getVariantStyles = (variant: VariantType): ClassNameValue => {
	switch (variant) {
		case 'primary':
			return 'text-white bg-gradient-to-b from-background-gradient-start to-background-gradient-end ring-none hover:bg-gradient-to-b hover:from-accent-hover hover:to-accent-hover disabled:bg-gradient-to-b disabled:from-accent-disabled disabled:to-accent-disabled'
		case 'secondary':
			return 'ring-1 ring-stroke-light dark:ring-stroke-light-dark text-text-main fill-text-main dark:text-text-main-dark  dark:fill-text-main-dark bg-white dark:bg-background-dark hover:bg-background-light hover:ring-stroke-light disabled:bg-stroke-light dark:disabled:bg-stroke-light-dark dark:disabled:text-text-secondary hover:dark:bg-background-dark-light'
		case 'tertiary':
			return 'ring-1 ring-stroke-light text-text-main fill-text-main bg-stroke-light hover:bg-stroke-light-hover disabled:bg-stroke-dark disabled:text-text-secondary dark:ring-stroke-light-dark dark:text-text-main-dark dark:fill-text-main-dark dark:bg-background-dark hover:dark:bg-stroke-light-hover dark:hover:text-text-main'
		case 'transparent':
			return 'ring-none text-text-main bg-white dark:bg-background-dark fill-text-main hover:bg-stroke-light-hover'
		case 'link':
			return 'text-accent bg-transparent fill-text-accent hover:text-text-main underline h-fit p-0'
		default:
			return ''
	}
}

const Button = ({
	label,
	leftIcon = null,
	rightIcon = null,
	variant = 'primary',
	className,
	classNameWrapper,
	classNameLabel,
	classNameContent,
	disabled = false,
	loading = false,
	loadingLabel = '',
	tooltip = null,
	tooltipPosition = 'top',
	tooltipOnDisabled = false,

	onClick,
	...rest
}: ButtonProps) => {
	const { isApp } = useContext(AppContext)

	const variantStyles = getVariantStyles(variant)

	const button = (
		<MantineButton
			className={cn(
				'rounded-lg flex px-3.5 capitalize items-center h-10 overflow-hidden relative font-semibold text-sm duration-default justify-center',
				!loading && 'disabled:pointer-events-none',
				tooltipOnDisabled && (disabled || loading) && 'pointer-events-none',
				// variantStyles,
				className
			)}
			classNames={{
				root: cn(
					'rounded-lg w-fit flex capitalize items-center gap-1 h-10 overflow-hidden relative disabled:opacity-100',
					variantStyles,
					className
				),
			}}
			disabled={disabled || loading}
			onClick={(e) => {
				e?.stopPropagation()
				onClick?.(e)
			}}
			{...rest}
		>
			{loading ? (
				<div className='flex items-center justify-center gap-2'>
					<Loader
						size={15}
						className={cn('border-black dark:border-white border-b-transparent dark:border-b-transparent',
							variant === 'primary' && 'border-white border-b-transparent'
						)}
					/>
					{!!loadingLabel && <span className={cn('text-center text-black dark:text-white', classNameLabel)}>{loadingLabel}</span>}
				</div>
			) : (
				<div
					className={cn(
						'flex items-center justify-center w-fit gap-1',
						classNameContent
					)}
				>
					{leftIcon && <span className="flex items-center">{leftIcon}</span>}
					{label && (
						<span className={cn('text-center', classNameLabel)}>{label}</span>
					)}
					{rightIcon && <span className="flex items-center">{rightIcon}</span>}
				</div>
			)}
		</MantineButton>
	)

	const tooltipContent =
		tooltipOnDisabled && (disabled || loading) ? (
			<span className="inline-block w-fit">{button}</span>
		) : (
			button
		)

	return tooltip && !isApp ? (
		<Tooltip
			label={tooltip}
			position={tooltipPosition}
			style={{
				maxWidth: tooltip?.length < 10 ? 50 : 200,
			}}
			className={cn(
				'fixed z-[100] font-normal bg-text-secondary/90 text-white mx-auto py-1.5 px-3 rounded text-xs text-wrap w-fit',
				classNameWrapper
			)}
			withArrow
			offset={variant === 'transparent' ? -10 : 1}
			isButton
		>
			{tooltipContent}
		</Tooltip>
	) : (
		button
	)
}

export default Button
